import React, { useEffect } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { AsyncPaginate } from "react-select-async-paginate";
import { selectCustomStyles } from "components/common/Select";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import ErrorMessage from "components/common/ErrorMessage";
import PhoneInput from "components/common/PhoneInput";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { feedbackVerfication } from "slices/appointmentSlice";
import { useDispatch } from "react-redux";
import { submitFeedback } from "slices/appointmentSlice";
import { useSelector } from "react-redux";
import FullPageLoader from "components/common/FullPageLoader";
import { appointmentsSelector } from "slices/appointmentSlice";

const FeedbackForm = ({ logoUrl, onSubmitCallback }) => {
  const { loader } = useSelector(appointmentsSelector);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const dispatch = useDispatch();
    
  const methods = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      rating: "",
      comments: "",
      suggestions: "",
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = methods;
  
  useEffect(() => {
    if (!token) {
        toast.error("Invalid feedback link");
        return;
    }

    dispatch(
        feedbackVerfication(token, (success, message, data) => {
        if (!success) {
            toast.error(message || "Invalid or expired feedback link");
            return;
        }

        if (data) {
            setValue("email", data.email || "");
            setValue("name", data.name || "");
            setValue("phone", data.phone || "");
        }
        })
    );
  }, [token, dispatch]);


  const onSubmit = (data) => {
    const payload = {
        token,
        ...data,
    };

    dispatch(
        submitFeedback(payload, (success, message) => {
        if (!success) {
            toast.error(message || "Failed to submit feedback");
            return;
        }

        toast.success("Thank you for your feedback!");
        methods.reset();
        })
    );
  };

    return (
    <>
    {loader && <FullPageLoader />}
    <div className="vh-100 mb-5">
        <div className="d-flex justify-content-center align-items-center flex-column h-100">
            <div>
                <img src="/images/logo-fill-second.png" alt="react-logo test" style={{ height:'auto', width: '397px'}}/>
            </div>
        <FormProvider {...methods}>
        <form
            className="card_table p-4 w-100"
            onSubmit={handleSubmit(onSubmit)}
            style={{ maxWidth: 600, margin: "0 auto" }}
        >
            {/* Logo at top */}
            {logoUrl && (
            <div className="text-center mb-4">
                <img
                src={logoUrl}
                alt="Logo"
                style={{ maxHeight: 80, objectFit: "contain" }}
                />
            </div>
            )}

            {/* Name */}
            <div className="row-primary flex-column">
            <h2>Name *</h2>
            <div className="w-100">
                <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                    <input
                    className="input-in type_inp"
                    type="text"
                    placeholder="Your name"
                    {...field}
                    />
                )}
                />
                {errors?.name?.message && (
                <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.name?.message} />
                </div>
                )}
            </div>
            </div>

            {/* Email */}
            <div className="row-primary flex-column">
            <h2>Email *</h2>
            <div className="w-100">
                <Controller
                name="email"
                control={control}
                rules={{
                    required: "Email is required",
                    pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email format",
                    },
                }}
                render={({ field }) => (
                    <input
                    className="input-in type_inp"
                    type="email"
                    placeholder="Your email"
                    {...field}
                    />
                )}
                />
                {errors?.email?.message && (
                <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.email?.message} />
                </div>
                )}
            </div>
            </div>

            {/* Phone */}
            <div className="row-primary flex-column">
            <h2>Phone</h2>
            <div className="w-100">
                <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                    <PhoneInput
                    className="input-in type_inp"
                    placeholder="Phone number"
                    {...field}
                    />
                )}
                />
            </div>
            </div>

            {/* Rating */}
            <div className="row-primary flex-column">
            <h2>Rating *</h2>
            <div className="w-100">
                <Controller
                name="rating"
                control={control}
                rules={{ required: "Please select a rating" }}
                render={({ field }) => (
                    <select className="input-in type_inp" {...field}>
                    <option value="">Select rating</option>
                    <option value="1">1 - Poor</option>
                    <option value="2">2 - Fair</option>
                    <option value="3">3 - Good</option>
                    <option value="4">4 - Very Good</option>
                    <option value="5">5 - Excellent</option>
                    </select>
                )}
                />
                {errors?.rating?.message && (
                <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.rating?.message} />
                </div>
                )}
            </div>
            </div>

            {/* Comments */}
            <div className="row-primary flex-column">
            <h2>Comments</h2>
            <div className="w-100">
                <Controller
                name="comments"
                control={control}
                render={({ field }) => (
                    <textarea
                    className="input-in type_inp"
                    placeholder="Write your comments here"
                    rows={4}
                    {...field}
                    />
                )}
                />
            </div>
            </div>

            {/* Suggestions */}
            <div className="row-primary flex-column">
            <h2>Suggestions</h2>
            <div className="w-100">
                <Controller
                name="suggestions"
                control={control}
                render={({ field }) => (
                    <textarea
                    className="input-in type_inp"
                    placeholder="Any suggestions for improvement"
                    rows={4}
                    {...field}
                    />
                )}
                />
            </div>
            </div>

            {/* Submit Button */}
            <div className="Form-footer footer_fullpage mt-4">
            <SubmitButton btnText="Submit Feedback" className="w-100" disabled={loader} />
            </div>
        </form>
        </FormProvider>
        </div>
    </div>
    </>
  );
};

export default FeedbackForm;
